import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { ButtonLeadingIcon, IconButton, PrimaryButton } from '../components/Button'
import Table from '../components/Table'
import Radom, { IPaymentLink } from '../state/Radom'
import { Link, useNavigate } from 'react-router-dom'
import { RADOM_COLORS } from '../util/Constants'
import {
  errorToast,
  infoToast,
  SecondsToPaymentPeriod,
  successToast
} from '../util/Util'
import Dropdown, { DropdownItem } from '../components/Dropdown'
import Dots from '../icons/Dots'
import { TextInput } from '../components/Input'
import Copy from '../icons/Copy'
import PageHeader from '../components/PageHeader'
import ConfirmButtonModal from '../components/ConfirmButtonModal'
import LinkExternal from '../components/LinkExternal'
import TimeText from '../components/TimeText'
import { Currencies } from '../util/Currencies'
import { Container } from '../components/Animations'
import PaymentMethodDisplay from '../components/PaymentMethodDisplay'
import Pagination from '../components/Pagination'
import Checkbox from '../components/Checkbox'
import {
  DropdownLabel,
  ListContent,
  ListControls,
  ListPage,
  ListPaginationRow,
  PageActionRow,
  PageHeaderRow,
  PageHeaderStack
} from '../components/DashboardPrimitives'
import Gate from '../components/Gate'

const CloseFns = {}

enum PaymentLinkStatusFilter {
  Active = 'Active',
  Archived = 'Archived'
}

const PaymentLinks = observer((): React.ReactElement => {
  const navigate = useNavigate()
  const [paymentLinks, setPaymentLinks] = useState<IPaymentLink[]>([])
  const [isLoadingPaymentLinks, setIsLoadingPaymentLinks] = useState(false)
  const pageSize = 20
  const [curPage, setCurPage] = useState(0)
  const [numPages, setNumPages] = useState(0)
  const [linkStatuses, setLinkStatuses] = useState<PaymentLinkStatusFilter[]>([
    PaymentLinkStatusFilter.Active,
    PaymentLinkStatusFilter.Archived
  ])
  const inputRefs = {}

  const loadPaymentLinks = (page: number, statuses: PaymentLinkStatusFilter[]): void => {
    const isArchivedValues = [
      ...(statuses.includes(PaymentLinkStatusFilter.Active) ? [false] : []),
      ...(statuses.includes(PaymentLinkStatusFilter.Archived) ? [true] : [])
    ]

    if (isArchivedValues.length === 0) {
      setPaymentLinks([])
      setNumPages(0)
      setCurPage(page)
      return
    }

    setIsLoadingPaymentLinks(true)
    Radom.listPaymentLinks([], pageSize, page * pageSize, 'updated_at', 'DESC', isArchivedValues)
      .then((res) => {
        setNumPages(Math.ceil(res.total / pageSize))
        setPaymentLinks(res.data)
        setCurPage(page)
      })
      .catch((err) => errorToast(err.message))
      .finally(() => setIsLoadingPaymentLinks(false))
  }

  useEffect(() => {
    loadPaymentLinks(0, linkStatuses)
  }, [linkStatuses])

  const onPageClick = (i: number): void => {
    loadPaymentLinks(i, linkStatuses)
  }

  const visiblePaymentLinks = paymentLinks.filter((p) => !!p.gateway.managed)

  return (
    <Container>
      <ListPage>
        <PageHeaderRow>
          <PageHeaderStack>
            <PageHeader
              title="Payment links"
            />
            <ListControls>
              <Dropdown
                selectedContent={<DropdownLabel>Status</DropdownLabel>}
                dropdownContent={<div>
                  {Object.values(PaymentLinkStatusFilter).map(s =>
                    <DropdownItem
                      key={s}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 14 }}
                      onClick={() => {
                        setLinkStatuses((prev) =>
                          prev.includes(s)
                            ? prev.filter((x) => x !== s)
                            : [...prev, s])
                      }}>
                      <Checkbox size={16} checked={linkStatuses.includes(s)} />
                      <span>{s}</span>
                    </DropdownItem>
                  )}
                </div>} />
            </ListControls>
          </PageHeaderStack>
          <Gate permission='WritePaymentLinks'>
            <PageActionRow>
              <Link
                key="createPaymentLink"
                to="/payment_links/create"
                style={{ textDecoration: 'none' }}
              >
                <PrimaryButton>
                  <ButtonLeadingIcon>+</ButtonLeadingIcon>
                  <span>Create payment link</span>
                </PrimaryButton>
              </Link>
            </PageActionRow>
          </Gate>
        </PageHeaderRow>
        <ListContent>
          <Table
            borderless
            headers={[
              <span key="URL">URL</span>,
              <span key="products">Products</span>,
              <span key="tokens ">Payment methods</span>,
              <span key="created">Created</span>,
              <span key="archived">Archived</span>,
              <span key="action"></span>
            ]}
            isLoading={isLoadingPaymentLinks}
            items={visiblePaymentLinks
              .map((p) => {
                const isArchived = !!p.isArchived
                const rowOpacity = isArchived ? 0.55 : 1
                return [
                  <div
                    key="url"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      gap: '5px',
                      opacity: rowOpacity
                    }}
                  >
                    <TextInput
                      ref={(r) => r && (inputRefs[p.url] = r)}
                      value={p.url}
                      style={{ maxWidth: '280px', height: 'fit-content' }}
                      disabled
                    />
                    <IconButton style={{ padding: 0 }}>
                      <Copy
                        style={{ width: 20 }}
                        onClick={(e) => {
                          inputRefs[p.url].select()
                          inputRefs[p.url].setSelectionRange(0, 99999)
                          navigator.clipboard.writeText(inputRefs[p.url].value)
                          infoToast('Copied payment link!')
                          e.preventDefault()
                          e.stopPropagation()
                        }}
                      />
                    </IconButton>
                    <IconButton
                      style={{ padding: 0 }}
                      onClick={(e) => {
                        if (!isArchived) {
                          window.open(p.url, '_blank')
                        }
                        e.preventDefault()
                        e.stopPropagation()
                      }} >
                      <LinkExternal stroke={RADOM_COLORS.GRAY_DARK} width={20} />
                    </IconButton>
                  </div>,
                  p.customizations?.allowFreeInputAmount
                    ? (
                      <span style={{ color: RADOM_COLORS.GRAY_DARKER, opacity: rowOpacity }}>
                    Custom payment amount
                      </span>
                    )
                    : (
                      <div
                        key="products"
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 10,
                          flexWrap: 'wrap',
                          opacity: rowOpacity
                        }}
                      >
                        {p.products.map((p) => (
                          <div
                            key={p.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              borderRadius: 5,
                              gap: 5,
                              padding: '5px 10px'
                            }}
                          >
                            {p.imageUrl && (
                              <img
                                src={p.imageUrl}
                                style={{ width: 20, height: 'auto' }}
                              />
                            )}
                            <span
                              style={{
                                maxWidth: 100,
                                whiteSpace: 'pre',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {p.name}
                            </span>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 3
                              }}
                            >
                              {
                                Currencies.find(
                                  (c) =>
                                    c.ticker.toLowerCase() ===
                                p.currency.toLowerCase()
                                )?.icon
                              }
                              <span>
                                {new Intl.NumberFormat('en-US', {
                                  maximumFractionDigits: 12
                                }).format(p.price)}
                              </span>
                            </div>
                            {p.chargingIntervalSeconds !== undefined &&
                          p.chargingIntervalSeconds > 0 && (
                              <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>
                              per{' '}
                                {SecondsToPaymentPeriod(
                                  p.chargingIntervalSeconds
                                ).toLowerCase()}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    ),

                  <div
                    key="paymentMethods"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      flexWrap: 'wrap',
                      opacity: rowOpacity
                    }}
                  >
                    {p.gateway.managed?.methods.slice(0, 3).map((m, i) => (
                      <PaymentMethodDisplay
                        key={i}
                        method={m}
                        excludeName
                        disableTooltip={false}
                      />
                    ))}
                    {p.gateway.managed &&
                    p.gateway.managed.methods.length > 3 && (
                      <span style={{ marginLeft: 4 }}>
                        +{p.gateway.managed.methods.length - 3}
                      </span>
                    )}
                  </div>,
                  <div key="created" style={{ color: RADOM_COLORS.GRAY_DARKER, opacity: rowOpacity }}>
                    <TimeText date={new Date(p.createdAt)} />
                  </div>,
                  <div key="archived" style={{ opacity: rowOpacity }}>
                    {isArchived ? 'Yes' : 'No'}
                  </div>,
                  <div key="dots" style={{ display: 'flex' }} onClick={((e) => e.stopPropagation())}>
                    <Gate permission="WritePaymentLinks">
                      <Dropdown
                        key={p.id}
                        noChevron
                        iconTrigger
                        selectedContent={<Dots width={15} />}
                        onCloseFn={(f) => {
                          CloseFns[p.id] = f
                        }}
                        dropdownContent={
                          <div style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                            {!isArchived && <DropdownItem
                              onClick={() => navigate(`/payment_links/${p.id}`)}
                            >
                          Edit payment link
                            </DropdownItem>}
                            {!isArchived && <ConfirmButtonModal
                              title="Archive payment link"
                              description="Are you sure you want to archive this payment link? It will no longer be available on public pay pages."
                              action="Archive payment link"
                              onConfirm={async () => {
                                await Radom.archivePaymentLink(p.id)
                                  .then(() => {
                                    successToast(
                                      'Successfully archived payment link'
                                    )
                                    loadPaymentLinks(curPage, linkStatuses)
                                  })
                                  .catch((err) => {
                                    errorToast(
                                      err.response?.data?.error || err.message
                                    )
                                  })
                              }}
                              ActionComponent={(props) => (
                                <DropdownItem {...props}>
                              Archive payment link
                                </DropdownItem>
                              )}
                            />}
                            <ConfirmButtonModal
                              title="Delete payment link"
                              description="Are you sure you want to delete this payment link? This action cannot be undone."
                              action="Delete payment link"
                              onConfirm={async () => {
                                await Radom.deletePaymentLink(p.id)
                                  .then(() => {
                                    successToast(
                                      'Successfully deleted payment link'
                                    )
                                    loadPaymentLinks(curPage, linkStatuses)
                                  })
                                  .catch((err) => {
                                    errorToast(
                                      err.response?.data?.error || err.message
                                    )
                                  })
                              }}
                              ActionComponent={(props) => (
                                <DropdownItem {...props}>
                              Delete payment link
                                </DropdownItem>
                              )}
                            />

                            {!isArchived && p.isPresale && (
                              <DropdownItem
                                onClick={() => {
                                  navigator.clipboard.writeText(
                                    `<iframe allow="clipboard-write" style="outline: 0; border: 0; width: 450px; height: 700px;" src="${p.url}" />`
                                  )
                                  successToast('Embed code copied')
                                  CloseFns[p.id]?.()
                                }}
                              >
                            Copy embed code
                              </DropdownItem>
                            )}
                          </div>
                        }
                      />
                    </Gate>
                  </div>
                ]
              })}
            emptyPlaceholderText="No payment links created yet"
            onClickRow={(i: number) => {
              const link = visiblePaymentLinks[i]
              if (!link || link.isArchived) {
                return
              }
              navigate(`/payment_link/detail/${link.id}`)
            }}
          />
          <ListPaginationRow>
            <Pagination
              curPage={curPage}
              numPages={numPages}
              onPageClick={onPageClick}
            />
          </ListPaginationRow>
        </ListContent>
      </ListPage>
    </Container>
  )
})

export default PaymentLinks
